// HTML from outside the app on its way into the compose window: the email
// being replied to or forwarded, a draft reopened from Outlook, pasted
// content, the person's OnPoint signature.
//
// The compose body is a live contentEditable on our own origin — unlike the
// reading pane, which sandboxes email in an iframe. So anything in that HTML
// that can run code (a <script>, an onerror= on an image, a javascript: link,
// an <iframe>, an SVG animation that rewrites a link) would run as the person
// signed in, the moment they press Reply. Stripping only <script> tags, as
// this used to, left the rest open to anyone who could send us an email.
//
// DOMPurify does the work, in an inert document, so nothing in the input
// loads or runs while it is being cleaned. What stays: layout and tables,
// inline styles, images (http, data: and cid:), ordinary links, data-*
// markers (data-compose-quote, data-pp-signature…). What else goes, because it is never needed in an email
// body and does harm in a live page: <style> (it would restyle the whole
// app — inline the rules first), <link>, <meta> and <base> (they can reload
// or redirect the page), form controls, and HTML comments (Outlook's
// conditional comments among them — DOMPurify drops comments that contain
// markup, a guard against parsing tricks).
//
// Inline styles stay, and they can still position things: a quoted email with
// `position:fixed; inset:0` would paint a page-sized fake over the app. That is
// kept in by the editor, not here: ComposeModal.module.css gives .body
// `contain: paint`, which makes it the containing block for anything
// positioned inside it and clips what falls outside.
import DOMPurify from 'dompurify';

const FORBID_TAGS = ['style', 'link', 'meta', 'base', 'form', 'input', 'button', 'select', 'textarea', 'option', 'optgroup'];

const CONFIG = {
  FORBID_TAGS,
  // contenteditable goes from everything that comes from outside. Left on
  // anything it makes an uneditable island in the editor, where a link is
  // followed by a plain click (the app's own tab to the sender's page). Not
  // even on what looks like a mention chip: data-mention is the sender's to
  // write too. Real chips are made by the window itself (insertMention).
  FORBID_ATTR: ['contenteditable'],
  // An id or name in a quoted email could otherwise take over a global the
  // app reads (window.__ONPOINT_URL__ and the like): DOMPurify prefixes them.
  SANITIZE_NAMED_PROPS: true,
};

/** html with anything that could run code taken out, as a string. */
export function sanitizeHtml(html) {
  if (html === null || html === undefined || html === '') return html;
  return DOMPurify.sanitize(String(html), CONFIG);
}

/**
 * The same, as nodes ready to insert: no second parse of the HTML on the
 * way into the page (so no chance of it parsing differently there).
 */
export function sanitizedFragment(html) {
  return DOMPurify.sanitize(String(html || ''), { ...CONFIG, RETURN_DOM_FRAGMENT: true });
}
